import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { HiOutlineArrowUpRight, HiOutlinePencilSquare, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi2';
import PageContainer from '../page-container';
import Button from '../button/button';
import FormLoader from '../form-loader/form-loader';
import Message from '../toast-message';

// Shared card layout for the workflow and sub-agent collections.
export default function ResourceListPage({ title, singular, subtitle, emptyDescription, icon: Icon, list, basePath, deleteItem, renderEditor }) {
    const { appId, dataList: items, loading, error, refetch } = list;
    const navigate = useNavigate();
    const [editor, setEditor] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const handleDelete = async () => {
        if (deleteBusy || !deleting) return;
        setDeleteBusy(true);
        setDeleteError('');
        try {
            const result = await deleteItem(appId, deleting.id);
            if (!(result?.status >= 200 && result?.status < 300)) {
                throw new Error(`Could not delete the ${singular.toLowerCase()}. Please try again.`);
            }
            setDeleting(null);
            await refetch();
        } catch (e) {
            setDeleteError(e.message || 'Could not delete this item. Please try again.');
        } finally {
            setDeleteBusy(false);
        }
    };

    const newLabel = `New ${singular.toLowerCase()}`;
    return (
        <PageContainer title={title} subtitle={subtitle} headerAction={<Button icon={<HiOutlinePlus size={16} />} text={newLabel} onClick={() => setEditor({})} />}>
            {error && <Message text={`Could not load ${title.toLowerCase()}.`} type="failure" />}
            {loading ? <FormLoader relative text={`Loading ${title.toLowerCase()}`} /> : error ? (
                <Button text="Try again" color="default" onClick={refetch} />
            ) : items.length === 0 ? (
                <div className="mt-10 rounded-2xl border border-dashed border-gray-200 px-6 py-12 text-center">
                    <Icon className="mx-auto text-gray-300" size={36} />
                    <h3 className="mt-3 text-base font-semibold text-gray-800">No {title.toLowerCase()} yet</h3>
                    <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">{emptyDescription}</p>
                    <div className="mt-5 flex justify-center"><Button text={newLabel} onClick={() => setEditor({})} /></div>
                </div>
            ) : (
                <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {items.map(item => (
                        <article key={item.id} className="group flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:border-gray-300 hover:shadow-md">
                            <button type="button" onClick={() => navigate(`/app/${appId}${basePath}/${item.id}`)} className="flex-1 rounded-t-2xl p-5 text-left focus:outline-none focus:ring-2 focus:ring-primary/20">
                                <div className="mb-5 flex items-center justify-between">
                                    <span className="rounded-xl bg-gray-100 p-2.5 text-primary"><Icon size={22} /></span>
                                    <HiOutlineArrowUpRight className="text-gray-400 transition group-hover:text-primary" size={18} />
                                </div>
                                <h3 className="truncate font-semibold text-gray-900">{item.name || item.id}</h3>
                                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-gray-500">{item.description || emptyDescription}</p>
                            </button>
                            <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
                                <span className="text-xs font-medium text-gray-500">{singular}</span>
                                <div className="flex gap-1">
                                    <button type="button" aria-label={`Edit ${item.name || item.id}`} onClick={() => setEditor(item)} className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-primary focus:ring-2 focus:ring-primary/20"><HiOutlinePencilSquare size={15} /> Edit</button>
                                    <button type="button" aria-label={`Delete ${item.name || item.id}`} onClick={() => { setDeleting(item); setDeleteError(''); }} className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-gray-500 hover:bg-rose-50 hover:text-rose-600 focus:ring-2 focus:ring-rose-200"><HiOutlineTrash size={15} /> Delete</button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}
            {editor && renderEditor({ item: editor, onCancel: () => setEditor(null), onCompletion: () => { setEditor(null); refetch(); } })}
            {deleting && (
                <Dialog open onClose={() => { if (!deleteBusy) setDeleting(null); }} className="relative z-50">
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                            <Dialog.Title className="text-lg font-semibold text-gray-900">Delete {singular.toLowerCase()}?</Dialog.Title>
                            <Dialog.Description className="mt-2 text-sm text-gray-500">“{deleting.name || deleting.id}” will be permanently deleted. This cannot be undone.</Dialog.Description>
                            {deleteError && <p role="alert" className="mt-3 text-sm text-rose-600">{deleteError}</p>}
                            <div className="mt-6 flex justify-end gap-2">
                                <Button color="default" text="Cancel" disabled={deleteBusy} onClick={() => setDeleting(null)} />
                                <Button color="error" text={`Delete ${singular.toLowerCase()}`} disabled={deleteBusy} loading={deleteBusy} onClick={handleDelete} />
                            </div>
                        </Dialog.Panel>
                    </div>
                </Dialog>
            )}
        </PageContainer>
    );
}
